import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertSimulationEngine = () => {
  const [isSimulating, setIsSimulating] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);

  const alertTemplates = [
    {
      id: 1,
      type: "critical",
      title: "Urgent: Medicare Coverage Restriction",
      message: "CMS has issued immediate coverage restrictions for Class III cardiac devices. Requires action within 48 hours.",
      category: "cardiovascular",
      priority: "critical",
      icon: "AlertTriangle"
    },
    {
      id: 2,
      type: "high",
      title: "Prior Authorization Update",
      message: "Blue Cross Blue Shield updated prior auth requirements for orthopedic implants effective next month.",
      category: "orthopedic",
      priority: "high",
      icon: "AlertCircle"
    },
    {
      id: 3,
      type: "medium",
      title: "Reimbursement Rate Change",
      message: "Aetna announced quarterly reimbursement adjustments for surgical devices. Review impact on pricing strategy.",
      category: "surgical",
      priority: "medium",
      icon: "Info"
    },
    {
      id: 4,
      type: "low",
      title: "Policy Documentation Update",
      message: "United Healthcare published updated documentation guidelines for diagnostic imaging devices.",
      category: "diagnostic",
      priority: "low",
      icon: "FileText"
    }
  ];

  const startSimulation = () => {
    setIsSimulating(true);
    setActiveAlerts([]);
    setAlertCount(0);

    let count = 0;
    const interval = setInterval(() => {
      if (count < alertTemplates?.length) {
        const newAlert = {
          ...alertTemplates?.[count],
          timestamp: new Date()?.toLocaleTimeString(),
          id: Date.now() + count
        };
        setActiveAlerts(prev => [newAlert, ...prev]);
        setAlertCount(prev => prev + 1);
        count++;
      } else {
        clearInterval(interval);
        setIsSimulating(false);
      }
    }, 2000);
  };

  const stopSimulation = () => {
    setIsSimulating(false);
  };

  const clearAlerts = () => {
    setActiveAlerts([]);
    setAlertCount(0);
  };

  const getPriorityStyles = (priority) => {
    switch(priority) {
      case 'critical':
        return 'bg-destructive/10 border-destructive text-destructive';
      case 'high':
        return 'bg-warning/10 border-warning text-warning';
      case 'medium':
        return 'bg-accent/10 border-accent text-accent';
      case 'low':
        return 'bg-success/10 border-success text-success';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  return (
    <section className="bg-muted py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
            Real-Time Alert Simulation
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Experience how PolicyAlerts delivers instant notifications for critical policy changes
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
                <div>
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                    Alert Dashboard
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Watch alerts appear in real-time as policy changes are detected
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!isSimulating ? (
                    <Button 
                      variant="default"
                      onClick={startSimulation}
                      iconName="Play"
                      iconPosition="left"
                    >
                      Start Simulation
                    </Button>
                  ) : (
                    <Button 
                      variant="destructive"
                      onClick={stopSimulation}
                      iconName="Square"
                      iconPosition="left"
                    >
                      Stop
                    </Button>
                  )}
                  {activeAlerts?.length > 0 && (
                    <Button 
                      variant="outline"
                      onClick={clearAlerts}
                      iconName="Trash2"
                      iconPosition="left"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-3 md:space-y-4 min-h-[400px]">
                {activeAlerts?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 md:py-16 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mb-4">
                      <Icon name="Bell" size={32} className="text-muted-foreground" />
                    </div>
                    <h4 className="text-base md:text-lg font-semibold text-foreground mb-2">
                      No Active Alerts
                    </h4>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Click "Start Simulation" to see how PolicyAlerts delivers real-time notifications
                    </p>
                  </div>
                ) : (
                  activeAlerts?.map((alert) => (
                    <div 
                      key={alert?.id}
                      className={`border-l-4 rounded-lg p-4 md:p-5 transition-all duration-300 ${getPriorityStyles(alert?.priority)}`}
                      style={{ animation: 'slideIn 0.3s ease-out' }}
                    >
                      <div className="flex items-start gap-3 md:gap-4">
                        <div className="flex-shrink-0 mt-1">
                          <Icon name={alert?.icon} size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                            <h4 className="text-sm md:text-base font-semibold text-foreground">
                              {alert?.title}
                            </h4>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {alert?.timestamp}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            {alert?.message}
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-background/50 rounded text-xs font-medium">
                              <Icon name="Tag" size={12} />
                              {alert?.category}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-background/50 rounded text-xs font-medium uppercase">
                              {alert?.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                Alert Statistics
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Alerts</span>
                  <span className="text-xl md:text-2xl font-bold text-foreground">{alertCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Active</span>
                  <span className="text-xl md:text-2xl font-bold text-success">{activeAlerts?.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <span className={`text-sm font-medium ${isSimulating ? 'text-success' : 'text-muted-foreground'}`}>
                    {isSimulating ? 'Running' : 'Stopped'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                Alert Priorities
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-destructive"></div>
                  <span className="text-sm text-muted-foreground flex-1">Critical</span>
                  <span className="text-sm font-medium text-foreground">Immediate Action</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-warning"></div>
                  <span className="text-sm text-muted-foreground flex-1">High</span>
                  <span className="text-sm font-medium text-foreground">24-48 Hours</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-accent"></div>
                  <span className="text-sm text-muted-foreground flex-1">Medium</span>
                  <span className="text-sm font-medium text-foreground">1 Week</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-success"></div>
                  <span className="text-sm text-muted-foreground flex-1">Low</span>
                  <span className="text-sm font-medium text-foreground">Informational</span>
                </div>
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 md:p-6">
              <div className="flex items-start gap-3">
                <Icon name="Lightbulb" size={20} color="var(--color-primary)" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    Pro Tip
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Configure custom alert rules to receive notifications only for policies affecting your specific device categories and payer networks.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default AlertSimulationEngine;